import { useCallback, useEffect, useRef, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  applyPromoCode,
  createBasket,
  createPreviewCustomer,
  lookupOffering,
  lookupVariant,
  reprice,
} from '../../src/forms/simpleCheckout/api';
import type { Country } from '../../src/forms/simpleCheckout/types';
import { hasPersistedCheckoutData } from '../../src/forms/simpleCheckout/useSimpleCheckout';
import { CheckoutFormBody } from './Form';
import { detectProduct, trackBeginCheckout, type DetectedProduct } from './tracking';
import type { OpenOptions } from './types';

type ResolvedOffering = { offeringId: string; marketingCode: string };

const FALLBACK_TITLE = 'trend-Abo';

// "€ 198,00" — leading symbol with a thin space matches the PO's spec
// ("€ 198,00 inkl. MwSt / Jahr"). Falls back to "{n} {currency}" if Intl
// isn't available (very old browsers).
function formatPrice(amount: number, currency: string): string {
  try {
    const parts = new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).formatToParts(amount);
    const symbol = parts.find((p) => p.type === 'currency')?.value ?? currency;
    const number = parts
      .filter((p) => p.type !== 'currency' && p.type !== 'literal')
      .map((p) => p.value)
      .join('');
    return `${symbol} ${number}`;
  } catch {
    return `${currency} ${amount.toFixed(2).replace('.', ',')}`;
  }
}

function formatPriceLine(amount: number, currency: string, period: string | null): string {
  const base = `${formatPrice(amount, currency)} inkl. MwSt`;
  return period ? `${base} / ${period}` : base;
}

export function App({ options, onClose }: { options: OpenOptions; onClose: () => void }) {
  const [resolved, setResolved] = useState<ResolvedOffering | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string>('EUR');
  const [period, setPeriod] = useState<string | null>(null);
  // Compax flags digital-only offerings (Top 500 access); those get no
  // "Versand inkludiert" line under the price.
  const [digital, setDigital] = useState(false);
  const [basketId, setBasketId] = useState<string | null>(null);
  const [basketUuid, setBasketUuid] = useState<string | null>(null);
  // Pulses on every price change after the first; the price line picks
  // this up via a CSS keyframe.
  const [priceFlashKey, setPriceFlashKey] = useState(0);
  const prevPriceRef = useRef<number | null>(null);
  // Snapshot of the initial offering-info pricing so we can snap back to
  // AT-correct values when the user toggles AT after experimenting with
  // DE/CH (avoids a second reprice round-trip for the default case).
  const initialPricingRef = useRef<{ price: number | null; currency: string; period: string | null } | null>(null);
  // Cached customer-scoped access token from /api/checkout/create-preview-customer.
  // We create it lazily on the first switch to a non-AT country (Compax
  // requires a customer-scoped token for basket address updates), then keep
  // it for the lifetime of the modal so AT → DE → CH toggling only ever
  // creates one preview customer.
  const previewTokenRef = useRef<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [product, setProduct] = useState<DetectedProduct>({ currency: options.currency || 'EUR' });
  // Promo code checked at Compax; its discount comes off the price shown in the header.
  const [promo, setPromo] = useState<{ code: string; discount: number } | null>(null);

  const apiBase = options.apiBase || 'https://www.trend.at';
  const tracking = options.tracking ?? {};

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const detected = await detectProduct(options.variantId);
      if (cancelled) return;
      const merged: DetectedProduct = {
        value: options.value ?? detected.value,
        currency: options.currency ?? detected.currency,
        itemId: detected.itemId,
        itemName: detected.itemName,
      };
      setProduct(merged);
      trackBeginCheckout(tracking, merged);
    })();
    return () => {
      cancelled = true;
    };
    // Run once per modal open; deps are stable for the lifetime of the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (options.stripePublicKey) {
      // Resolves the Stripe SDK from a runtime option; async by nature.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStripePromise(loadStripe(options.stripePublicKey));
    } else {
      setResolveError('Stripe Public Key fehlt (stripePublicKey Option).');
    }
  }, [options.stripePublicKey]);

  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (options.offeringId) {
        // Look up the Statamic page so we can show its title in the header,
        // plus the Compax offering total so we can show a price line under
        // it. The offering itself is already known, so a failure here just
        // leaves title/price null and the modal degrades gracefully.
        const info = await lookupOffering(apiBase, options.offeringId);
        if (cancelled) return;
        if (info.success) {
          setResolved({ offeringId: info.offeringId, marketingCode: info.marketingCode });
          setTitle(info.title);
          setPrice(info.price);
          setCurrency(info.currency);
          setPeriod(info.period);
          setDigital(info.digital);
          setBasketId(info.basketId);
          setBasketUuid(info.basketUuid);
          prevPriceRef.current = info.price;
          initialPricingRef.current = {
            price: info.price,
            currency: info.currency,
            period: info.period,
          };
        } else {
          setResolved({ offeringId: options.offeringId, marketingCode: 'TREND' });
        }
        return;
      }
      if (!options.variantId) {
        if (!cancelled) setResolveError('offeringId oder variantId muss angegeben werden.');
        return;
      }
      const lookup = await lookupVariant(apiBase, options.variantId);
      if (cancelled) return;
      if (!lookup.success) {
        setResolveError(lookup.error);
      } else {
        setResolved({ offeringId: lookup.offeringId, marketingCode: lookup.marketingCode });
        setTitle(lookup.title);
        setPrice(lookup.price);
        setCurrency(lookup.currency);
        setPeriod(lookup.period);
        setDigital(lookup.digital);
        setBasketId(lookup.basketId);
        setBasketUuid(lookup.basketUuid);
        prevPriceRef.current = lookup.price;
        initialPricingRef.current = {
          price: lookup.price,
          currency: lookup.currency,
          period: lookup.period,
        };
      }
    }
    resolve().catch((e) => {
      if (!cancelled) setResolveError(e instanceof Error ? e.message : String(e));
    });
    return () => {
      cancelled = true;
    };
  }, [apiBase, options.offeringId, options.variantId]);

  // Called by the Form whenever the user changes the country dropdown.
  // - AT (default): no reprice needed — the productsOffering total returned
  //   at modal open is already AT-correct. Skip.
  // - DE/CH: shipping cost differs, so we lazily spin up an anonymous
  //   Compax customer (one per modal at most, cached) to get a token that
  //   authorizes the basket-address update, then reprice and flash.
  // Failures are swallowed silently — the previous price keeps showing,
  // the form still works, the contract still submits via the normal flow.
  const handleCountryChange = useCallback(
    async (country: Country) => {
      // Snap back to the initial AT pricing without a reprice round-trip.
      // (productsOffering returns AT-correct values, so we already have them
      // cached. Flash the price line if the snap actually changes anything.)
      if (country === 'AT') {
        const initial = initialPricingRef.current;
        if (!initial) return;
        if (initial.price !== null && prevPriceRef.current !== null && initial.price !== prevPriceRef.current) {
          setPriceFlashKey((k) => k + 1);
        }
        prevPriceRef.current = initial.price;
        setPrice(initial.price);
        setCurrency(initial.currency);
        setPeriod(initial.period);
        return;
      }
      try {
        // Ensure a basket exists to reprice against. The layover open path no
        // longer creates one (it serves cached display data), so on the first
        // switch away from AT we spin the basket up lazily and cache it in
        // state for any subsequent country toggles.
        let ids = basketId && basketUuid ? { basketId, basketUuid } : null;
        if (!ids) {
          if (!resolved) return;
          const created = await createBasket(apiBase, resolved.offeringId, resolved.marketingCode);
          if (!created.success) return;
          ids = { basketId: created.basketId, basketUuid: created.basketUuid };
          setBasketId(created.basketId);
          setBasketUuid(created.basketUuid);
        }
        if (!previewTokenRef.current) {
          const previewRes = await createPreviewCustomer(apiBase);
          if (!previewRes.success) return;
          previewTokenRef.current = previewRes.accessToken;
        }
        const res = await reprice(apiBase, {
          basketId: ids.basketId,
          basketUuid: ids.basketUuid,
          country,
          accessToken: previewTokenRef.current,
        });
        if (!res.success) return;
        if (res.price !== null) {
          if (prevPriceRef.current !== null && res.price !== prevPriceRef.current) {
            setPriceFlashKey((k) => k + 1);
          }
          prevPriceRef.current = res.price;
          setPrice(res.price);
          setCurrency(res.currency);
          // Keep the existing period unless the server returns a new one
          // (currency/period normally don't change on a country swap).
          if (res.period) setPeriod(res.period);
        }
      } catch {
        // Silent on failure — the previous price keeps showing.
      }
    },
    [apiBase, basketId, basketUuid, resolved],
  );

  // Called by the Form's Gutschein field; an empty code removes the applied one.
  const handleApplyPromo = useCallback(
    async (code: string): Promise<{ success: boolean; error?: string }> => {
      if (!code) {
        setPromo(null);
        return { success: true };
      }
      if (!resolved) return { success: false, error: 'Gutschein-Code konnte nicht geprüft werden.' };
      try {
        const res = await applyPromoCode(apiBase, {
          offeringId: resolved.offeringId,
          marketingCode: resolved.marketingCode,
          promotionalCode: code,
        });
        if (!res.success) return { success: false, error: res.error };
        setPromo({ code: res.promotionalCode, discount: res.discount });
        if (res.discount > 0) setPriceFlashKey((k) => k + 1);
        return { success: true };
      } catch {
        return { success: false, error: 'Gutschein-Code konnte nicht geprüft werden.' };
      }
    },
    [apiBase, resolved],
  );

  // Close request handler: if the user typed anything (persisted to
  // localStorage by useSimpleCheckout), prompt before dismissing. The data
  // itself is intentionally kept around so reopening rehydrates the form.
  const handleCloseRequest = useCallback(() => {
    if (hasPersistedCheckoutData()) {
      const ok = window.confirm(
        'Möchten Sie den Kauf wirklich abbrechen? Ihre Eingaben bleiben für später gespeichert.',
      );
      if (!ok) return;
    }
    onClose();
  }, [onClose]);

  return (
    <div
      // z-index has to clear typical Shopify theme chrome (~10k–100k) but stay
      // BELOW Stripe's 3DS challenge container (which Stripe.js renders at
      // z-index 99_999_999). 2_000_000 is comfortably between the two so the
      // challenge frame appears on top of our backdrop when triggered.
      className="fixed inset-0 z-[2000000] flex items-stretch justify-center bg-black/50 sm:items-center"
      onClick={handleCloseRequest}
    >
      <div
        className="wco-scroll shadow-2xl relative flex max-h-screen w-full max-w-[600px] flex-col bg-white sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray bg-white px-6 py-4">
          <div className="flex min-w-0 flex-col gap-1">
            <p
              style={{
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: 1.3,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#767676',
                margin: 0,
                padding: 0,
                fontFamily: 'inherit',
              }}
            >
              Ihre Bestellung
            </p>
            <h1
              // Inline styles so a host theme's generic `h1 { font-size; font-weight }`
              // rules can't blow up our header text (Shopify themes commonly do).
              // Inline specificity (1,0,0,0) beats every selector that isn't `!important`.
              style={{
                fontSize: '26px',
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: '0.01em',
                textTransform: 'uppercase',
                color: '#24364B',
                margin: 0,
                padding: 0,
                fontFamily: "'Barlow Condensed', 'Inter', sans-serif",
              }}
            >
              {title ?? FALLBACK_TITLE}
            </h1>
            {price !== null && (
              <>
                {/*
                  Inline <style> block carrying the flash keyframe — adding it
                  here (inside the host element) is host-CSS-immune the same way
                  our other inline styles are, and it scopes the animation to
                  this modal instance only. The keyframe pulses twice over
                  ~900ms to draw the eye when the country swap changes the
                  shipping cost (and therefore the total).
                */}
                <style>{`@keyframes wco-price-flash { 0%,100% { background-color: transparent; } 25%,75% { background-color: rgba(243,189,68,0.35); } }`}</style>
                <p
                  // `key` is bumped on every reprice so React remounts the
                  // node and the animation restarts cleanly even when the user
                  // toggles AT → DE → CH in quick succession.
                  key={priceFlashKey}
                  // Inline-styled for the same host-CSS-immunity reason.
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: '#24364B',
                    margin: 0,
                    padding: '2px 4px',
                    fontFamily: 'inherit',
                    animation: priceFlashKey > 0 ? 'wco-price-flash 900ms ease-in-out 1' : undefined,
                  }}
                >
                  {formatPriceLine(promo ? Math.max(price - promo.discount, 0) : price, currency, period)}
                </p>
                {promo && promo.discount > 0 && (
                  <p
                    style={{
                      fontSize: '13px',
                      lineHeight: 1.3,
                      color: '#767676',
                      margin: 0,
                      padding: 0,
                      fontFamily: 'inherit',
                    }}
                  >
                    statt <s>{formatPrice(price, currency)}</s> · Gutschein {promo.code}: −{' '}
                    {formatPrice(promo.discount, currency)}
                  </p>
                )}
              </>
            )}
            {price !== null && !digital && (
              <p
                style={{
                  fontSize: '13px',
                  lineHeight: 1.3,
                  color: '#767676',
                  margin: 0,
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                Versand inkludiert
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleCloseRequest}
            aria-label="Schließen"
            className="shrink-0 text-2xl leading-none text-gray-medium hover:text-primary"
          >
            ×
          </button>
        </header>

        {resolveError && <p className="bg-error/10 text-error p-4 text-sm">{resolveError}</p>}

        {!resolved && !resolveError && (
          <div className="flex items-center gap-3 p-6 text-sm text-gray-medium" role="status" aria-live="polite">
            {/*
              Inline-styled spinner + keyframe, host-CSS-immune the same way the
              header text is (a Shopify theme can't restyle inline rules without
              !important). Scoped to this modal instance.
            */}
            <style>{`@keyframes wco-spin { to { transform: rotate(360deg); } }`}</style>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 18,
                height: 18,
                border: '2px solid rgba(0,0,0,0.15)',
                borderTopColor: '#24364B',
                borderRadius: '50%',
                animation: 'wco-spin 0.7s linear infinite',
                flexShrink: 0,
              }}
            />
            Lade Bestelldaten …
          </div>
        )}

        {resolved && stripePromise && (
          <Elements stripe={stripePromise}>
            <CheckoutFormBody
              offeringId={resolved.offeringId}
              marketingCode={resolved.marketingCode}
              apiBase={apiBase}
              options={options}
              onClose={onClose}
              product={product}
              onCountryChange={handleCountryChange}
              promotionalCode={promo?.code ?? null}
              onApplyPromo={handleApplyPromo}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}
